import { useTheme } from "expo-router/react-navigation";
import { Trophy } from "lucide-react-native";
import { FC, useMemo } from "react";
import { View } from "react-native";

import {
  Heatmap,
  HorizontalBarChart,
  useCopyableChart,
} from "@/components/charts";
import { Box, Card, HStack, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export interface MonadicReportData {
  /** One per stimulus in `section.stimuli`. `id` is the stimulus
      index as a string; `name` is the display label (cooker passes
      the stimulus string verbatim — no i18n indirection). */
  concepts: { id: string; name: string }[];
  /** One per scoring question in the section, in display order.
      `label` is rendered as-is. */
  metrics: { id: string; label: string }[];
  /** Number of respondents per concept (length === concepts.length).
      Used as the heatmap denominator and the ranking tooltip n. */
  perCellSampleSize: number[];
  /** Distinct respondents across the whole section, deduped on
      `persona.id`. Used for any "(N respondents)" chart-title chip
      so we don't double-count people who saw multiple concepts. */
  respondentCount: number;
  /** Top-box COUNTS, indexed by `[conceptIdx][metricIdx]`. */
  topBoxCounts: number[][];
}

interface MonadicReportProps {
  data: MonadicReportData;
  /** Optional editorial summary (KEY INSIGHTS card) — shown only by
      the onboarding's sample modal. */
  showSampleEditorialSummary?: boolean;
}

/* ── Component ──────────────────────────────────────────── */

/**
 * Pure visualization — never touches the raw run results, never
 * routes labels through i18n. Reads its concepts/metrics/scores from
 * typed `data`; cooker layer (`MonadicSectionResults`) and onboarding
 * fixture (`buildSampleMonadicData`) are the only ones that decide
 * what those strings are.
 */
const MonadicReport: FC<MonadicReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n } = useLocalization();
  const theme = useTheme();
  const primary = theme.colors.primary;

  const { chartRef: rankingRef, copyButton: rankingCopyButton } =
    useCopyableChart();
  const { chartRef: heatmapRef, copyButton: heatmapCopyButton } =
    useCopyableChart({ raster: true });

  const fmtPct = (n: number) =>
    i18n.numberToPercentage(n, {
      precision: 1,
      stripInsignificantZeros: false,
    });
  const fmtPctRound = (n: number) =>
    i18n.numberToPercentage(n, { precision: 0 });

  /* Top-box rate (0..100) per concept × metric, derived from counts +
     per-cell sample sizes. Concepts with `n=0` collapse to 0 so an
     empty cell can't poison the composite ranking. */
  const topBoxRate: number[][] = useMemo(
    () =>
      data.concepts.map((_, ci) => {
        const n = data.perCellSampleSize[ci] ?? 0;
        return data.metrics.map(
          (_, mi) => (n > 0 ? (data.topBoxCounts[ci]?.[mi] ?? 0) / n : 0) * 100
        );
      }),
    [data]
  );

  /* Composite = average top-box rate across metrics; the canonical
     monadic "winner" measure. */
  const composite: number[] = useMemo(
    () =>
      topBoxRate.map((row) =>
        row.length === 0 ? 0 : row.reduce((s, r) => s + r, 0) / row.length
      ),
    [topBoxRate]
  );

  const winnerIdx = useMemo(() => {
    let best = 0;
    composite.forEach((v, i) => {
      if (v > composite[best]) best = i;
    });
    return best;
  }, [composite]);

  // Stimuli (concepts) are the heatmap rows, metrics the columns.
  // `topBoxCounts` is indexed `[conceptIdx][metricIdx]`, so with rowIdx =
  // concept and colIdx = metric the lookup is `topBoxCounts[rowIdx][colIdx]`.
  const heatmapCells = useMemo(
    () =>
      data.concepts.flatMap((_, rowIdx) =>
        data.metrics.map((_, colIdx) => ({
          column: colIdx,
          count: data.topBoxCounts[rowIdx]?.[colIdx] ?? 0,
          row: rowIdx,
        }))
      ),
    [data]
  );

  const heatmapDenominator = useMemo(
    () =>
      data.perCellSampleSize.length > 0
        ? Math.max(...data.perCellSampleSize)
        : 0,
    [data.perCellSampleSize]
  );

  /* Deduped respondent count — see `MonadicReportData.respondentCount`.
     We used to sum `perCellSampleSize` here, but `cells_per_respondent`
     can be > 1 (two concepts per persona is common), which double-
     counts each respondent in the headline chip. */
  const totalRespondents = data.respondentCount;

  /* No answers yet: render the shell, hand charts empty arrays (they show
     their own "No data available"), and show "—" instead of a fake winner —
     mirroring how `PricingResult` shows "—" rather than a fabricated price. */
  const hasData = totalRespondents > 0;

  const rankingPoints = useMemo(
    () =>
      data.concepts.map((c, idx) => ({
        count: data.perCellSampleSize[idx] ?? 0,
        label: c.name,
        tooltipLine: `${fmtPct(composite[idx])} · n = ${
          data.perCellSampleSize[idx] ?? 0
        }`,
        value: composite[idx],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, composite, i18n]
  );

  if (data.concepts.length === 0) {
    return null;
  }

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.monadic.sampleReport.summary.body")}
          headline={i18n.t("useCasePage.monadic.sampleReport.summary.headline")}
        />
      )}

      <Card className="p-6 rounded-2xl border border-outline-200">
        <VStack className="gap-3 lg:flex-row lg:items-center lg:gap-5">
          <VStack className="gap-2 lg:flex-1">
            <HStack className="items-center gap-4">
              <Box
                className="w-14 h-14 rounded-2xl items-center justify-center shrink-0"
                style={{ backgroundColor: primary }}
              >
                <Trophy color="white" size={26} strokeWidth={2.2} />
              </Box>
              <VStack className="flex-1 gap-1">
                <Text className="text-2xs font-semibold text-primary-600 uppercase tracking-wide">
                  {i18n.t("useCasePage.monadic.sampleReport.winner.badge")}
                </Text>
                <Text className="text-xl font-bold text-typography-900 tracking-tight">
                  {hasData ? data.concepts[winnerIdx].name : "—"}
                </Text>
              </VStack>
            </HStack>
            <Text className="text-xs text-typography-500 lg:pl-[72px]">
              {i18n.t("useCasePage.monadic.sampleReport.winner.subtitle")}
            </Text>
          </VStack>
          <VStack className="items-center lg:items-end gap-0">
            <Text
              className="text-3xl font-bold tracking-tight"
              style={{ color: primary }}
            >
              {hasData ? fmtPctRound(composite[winnerIdx]) : "—"}
            </Text>
            <Text className="text-2xs font-semibold text-typography-400 uppercase tracking-wide">
              {i18n.t("useCasePage.monadic.sampleReport.winner.compositeLabel")}
            </Text>
          </VStack>
        </VStack>
      </Card>

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {rankingCopyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.monadic.sampleReport.ranking.title")} (${i18n.t("dashboardNavigator.respondents", { count: totalRespondents })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.monadic.sampleReport.ranking.subtitle")}
            </Text>
          </VStack>
          <View ref={rankingRef} style={{ width: "100%" }}>
            <HorizontalBarChart points={hasData ? rankingPoints : []} />
          </View>
        </VStack>
      </Card>

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {heatmapCopyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.monadic.sampleReport.heatmap.title")} (${i18n.t("dashboardNavigator.respondents", { count: totalRespondents })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.monadic.sampleReport.heatmap.subtitle")}
            </Text>
          </VStack>
          <View ref={heatmapRef} style={{ width: "100%" }}>
            <Heatmap
              cells={hasData ? heatmapCells : []}
              columnLabels={hasData ? data.metrics.map((m) => m.label) : []}
              rowLabels={hasData ? data.concepts.map((c) => c.name) : []}
              totalRespondents={heatmapDenominator}
            />
          </View>
        </VStack>
      </Card>
    </VStack>
  );
};

export default MonadicReport;
